import React, { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// 定数の外部化
const CONSTANTS = {
  MAX_TITLE_LENGTH: 100,
  MAX_CONTENT_LENGTH: 10000,
  VARIABLE_PATTERN: /\{\{(\w+)\}\}/g,
  SUPPORTED_VARIABLES: ['name', 'company', 'product', 'price', 'date', 'time', 'url', 'email', 'phone', 'position']
}

// XSS対策のサニタイズ関数
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

export default function TemplateForm({ user, template, onSave, onCancel }) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [variables, setVariables] = useState([])
  const [saving, setSaving] = useState(false)
  const [copiedVar, setCopiedVar] = useState(null)
  const [categoryId, setCategoryId] = useState(null)
  const [categories, setCategories] = useState([])
  const contentRef = useRef(null)

  useEffect(() => {
    if (template) {
      setTitle(template.name || template.title || '') // Support both name and title for backward compatibility
      setContent(template.content)
      setCategoryId(template.category_id || null)
      // Variables will be loaded separately
    }
  }, [template])

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('template_categories')
          .select('*')
          .eq('user_id', user.id)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('Error fetching categories:', error);
        } else {
          setCategories(data || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, [user]);

  // Load variables separately when template changes
  // Temporarily disabled due to variables table not being created yet
  useEffect(() => {
    const loadVariables = async () => {
      if (template?.id) {
        // TODO: Re-enable when variables table is created in database
        console.log('Variables loading disabled - table not created yet')
        setVariables([])
        /*
        try {
          const { data: variablesData, error } = await supabase
            .from('variables')
            .select('*')
            .eq('template_id', template.id)
            .order('created_at')

          if (error) {
            console.error('Error loading variables:', error)
            setVariables([])
          } else {
            const formattedVariables = variablesData.map(v => ({
              id: v.id,
              name: v.name,
              label: v.description || v.name
            }))
            setVariables(formattedVariables)
            console.log('Variables loaded:', formattedVariables)
          }
        } catch (error) {
          console.error('Error loading variables:', error)
          setVariables([])
        }
        */
      } else {
        setVariables([])
      }
    }

    loadVariables()
  }, [template?.id])

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 's':
            e.preventDefault()
            if (!saving) {
              const form = e.target.closest('form')
              if (form) {
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
                form.dispatchEvent(submitEvent)
              }
            }
            break
          case 'Enter':
            if (e.shiftKey) {
              e.preventDefault()
              if (!saving) {
                const form = e.target.closest('form')
                if (form) {
                  const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
                  form.dispatchEvent(submitEvent)
                }
              }
            }
            break
          default:
            // その他のキーは何もしない
            break
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [saving])

  // Extract variables from content - メモ化でパフォーマンス改善
  const extractVariables = useCallback((text) => {
    const matches = text.match(CONSTANTS.VARIABLE_PATTERN)
    if (!matches) return []
    
    const uniqueVars = [...new Set(matches.map(match => match.replace(/[{}]/g, '')))]
    return uniqueVars
  }, [])

  // Update variables list when content changes
  useEffect(() => {
    const detectedVars = extractVariables(content)
    
    // Update existing variables and add new ones
    setVariables(prevVariables => {
      const updatedVariables = detectedVars.map(varName => {
        const existing = prevVariables.find(v => v.name === varName)
        return existing || { name: varName, label: varName }
      })
      return updatedVariables
    })
  }, [content, extractVariables]) // extractVariablesを依存関係に追加

  const updateVariableLabel = (index, label) => {
    const updated = [...variables]
    updated[index].label = label
    setVariables(updated)
  }

  // バリデーション関数
  const validateTemplate = () => {
    const errors = []
    
    if (!title.trim()) {
      errors.push('テンプレート名を入力してください')
    }
    
    if (title.length > CONSTANTS.MAX_TITLE_LENGTH) {
      errors.push(`テンプレート名は${CONSTANTS.MAX_TITLE_LENGTH}文字以内で入力してください`)
    }
    
    if (!content.trim()) {
      errors.push('メール本文を入力してください')
    }
    
    if (content.length > CONSTANTS.MAX_CONTENT_LENGTH) {
      errors.push(`メール本文は${CONSTANTS.MAX_CONTENT_LENGTH.toLocaleString()}文字以内で入力してください`)
    }
    
    // 変数名の重複チェック
    const varNames = variables.map(v => v.name)
    const duplicates = varNames.filter((name, index) => varNames.indexOf(name) !== index)
    if (duplicates.length > 0) {
      errors.push('重複する変数名があります: ' + duplicates.join(', '))
    }
    
    return errors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // バリデーション実行
    const validationErrors = validateTemplate()
    if (validationErrors.length > 0) {
      alert('入力内容に問題があります:\n' + validationErrors.join('\n'))
      return
    }
    
    setSaving(true)

    try {
      // Debug: Log user information
      console.log('Current user for save:', user)
      console.log('Template being saved:', template ? 'UPDATE' : 'INSERT')
      
      // Get current session to ensure we have the right user ID
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) {
        console.error('Session error:', sessionError)
        throw new Error('認証セッションの取得に失敗しました')
      }
      
      if (!session?.user) {
        throw new Error('ログインセッションが見つかりません。再度ログインしてください。')
      }
      
      console.log('Session user ID:', session.user.id)
      
      // Ensure we have a valid user ID - prefer session user ID
      const userId = session.user.id || user.id
      if (!userId) {
        throw new Error('ユーザーIDが取得できません。再度ログインしてください。')
      }

      const templateData = {
        user_id: userId,
        name: sanitizeInput(title.trim()), // Use 'name' column instead of 'title'
        content: sanitizeInput(content.trim()),
        category_id: categoryId
      }

      console.log('Template data to save:', templateData)

      let result
      if (template) {
        // Update existing template
        console.log('Updating template with ID:', template.id)
        result = await supabase
          .from('email_templates')
          .update(templateData)
          .eq('id', template.id)
          .select()
      } else {
        // Create new template
        console.log('Creating new template')
        result = await supabase
          .from('email_templates')
          .insert([templateData])
          .select()
      }

      if (result.error) {
        console.error('Template save error:', result.error)
        throw new Error(`テンプレートの保存に失敗しました: ${result.error.message}`)
      }

      const savedTemplate = result.data[0]
      console.log('Template saved successfully:', savedTemplate)

      // Save variables separately if they exist
      // Temporarily disabled due to variables table not being created yet
      if (variables.length > 0) {
        console.log('Variables detected but not saving due to table not created:', variables)
        // TODO: Re-enable when variables table is created in database
        /*
        const variableData = variables.map(v => ({
          user_id: userId,
          template_id: savedTemplate.id,
          name: sanitizeInput(v.name),
          description: sanitizeInput(v.label)
        }))

        console.log('Saving variables:', variableData)

        // Delete existing variables for this template
        if (template) {
          await supabase
            .from('variables')
            .delete()
            .eq('template_id', template.id)
        }

        // Insert new variables
        const variablesResult = await supabase
          .from('variables')
          .insert(variableData)
          .select()

        if (variablesResult.error) {
          console.error('Variables save error:', variablesResult.error)
          // Don't throw error for variables, just log it
          console.warn('Variables could not be saved, but template was saved')
        } else {
          console.log('Variables saved successfully:', variablesResult.data)
        }
        */
      }

      console.log('Database operation result:', result)

      if (result.error) {
        console.error('Database error details:', {
          message: result.error.message,
          details: result.error.details,
          hint: result.error.hint,
          code: result.error.code
        })
        
        // Handle specific error types
        if (result.error.code === '42501') {
          throw new Error('データベースへのアクセス権限がありません。管理者にお問い合わせください。')
        } else if (result.error.code === '23503') {
          throw new Error('ユーザー情報が見つかりません。再度ログインしてください。')
        } else {
          throw new Error(`保存エラー: ${result.error.message}`)
        }
      }

      if (result.data && result.data.length > 0) {
        console.log('Template saved successfully:', result.data[0])
        onSave(result.data[0]) // 保存されたテンプレートデータを渡す
      } else {
        console.warn('Save operation completed but no data returned')
        onSave() // Still call onSave as the operation might have succeeded
      }
    } catch (err) {
      console.error('Template save error:', err)
      alert(err.message || '予期しないエラーが発生しました')
    } finally {
      setSaving(false)
    }
  }

  // Quick variable buttons
  const quickVariables = [
    { name: 'name', label: '担当者名', icon: '👤' },
    { name: 'company', label: '会社名', icon: '🏢' },
    { name: 'product', label: '商品名', icon: '📦' },
    { name: 'price', label: '価格', icon: '💰' },
    { name: 'date', label: '日付', icon: '📅' },
    { name: 'time', label: '時間', icon: '⏰' },
    { name: 'url', label: 'URL', icon: '🔗' },
    { name: 'email', label: 'メールアドレス', icon: '✉️' },
    { name: 'phone', label: '電話番号', icon: '📞' },
    { name: 'position', label: '役職', icon: '💼' }
  ]

  const insertVariable = (varName) => {
    const textarea = contentRef.current
    if (!textarea) {
      console.error('Textarea reference not found')
      return
    }

    try {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const newContent = content.substring(0, start) + `{{${varName}}}` + content.substring(end)
      setContent(newContent)
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        if (textarea) {
          textarea.selectionStart = textarea.selectionEnd = start + varName.length + 4
          textarea.focus()
        }
      }, 0)
    } catch (err) {
      console.error('Error inserting variable:', err)
      alert('変数の挿入に失敗しました')
    }
  }

  const copyVariable = async (varName) => {
    try {
      await navigator.clipboard.writeText(`{{${varName}}}`)
      setCopiedVar(varName)
      setTimeout(() => setCopiedVar(null), 2000)
    } catch (err) {
      console.error('Copy error:', err)
      alert('変数のコピーに失敗しました')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              {template ? (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  テンプレート編集
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  新規テンプレート作成
                </>
              )}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Template Info */}
              <div className="space-y-6">
                {/* Title Input */}
                <div>
                  <label htmlFor="title" className="block text-sm font-semibold text-gray-800 mb-2">
                    テンプレート名
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-0 transition-colors duration-200"
                    placeholder="例: 新商品提案メール"
                  />
                </div>

                {/* Category Selector */}
                <div>
                  <label htmlFor="category" className="block text-sm font-semibold text-gray-800 mb-2">
                    カテゴリー
                  </label>
                  <select
                    id="category"
                    value={categoryId || ''}
                    onChange={(e) => setCategoryId(e.target.value || null)}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-0 transition-colors duration-200"
                  >
                    <option value="">カテゴリーなし</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Content Editor */}
                <div>
                  <label htmlFor="content" className="block text-sm font-semibold text-gray-800 mb-2">
                    メール本文
                  </label>
                  <div className="relative">
                    <textarea
                      ref={contentRef}
                      id="content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      required
                      rows={12}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-0 transition-colors duration-200 font-mono text-sm"
                      placeholder={`{{name}}様

いつもお世話になっております。
{{company}}の{{product}}について
ご案内させていただきます。

価格: {{price}}
詳細URL: {{url}}

よろしくお願いいたします。`}
                    />
                    <div className="absolute top-2 right-2 text-xs text-gray-400">
                      変数: {`{{変数名}}`}
                    </div>
                  </div>
                </div>

                {/* Quick Variables */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    クイック変数挿入
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {quickVariables.map(({ name, label, icon }) => (
                      <div key={name} className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => insertVariable(name)}
                          className="flex-1 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-blue-50 hover:to-blue-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 border border-gray-200 hover:border-blue-300 hover:shadow-sm"
                        >
                          <span className="text-base">{icon}</span>
                          <span>{label}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => copyVariable(name)}
                          className={`px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                            copiedVar === name
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                          }`}
                          title="変数をコピー"
                        >
                          {copiedVar === name ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Variable Settings */}
              <div className="space-y-6">
                {/* Detected Variables */}
                {variables.length > 0 && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      検出された変数
                    </h3>
                    <div className="space-y-3">
                      {variables.map((variable, index) => (
                        <div key={variable.name} className="bg-white rounded-lg p-3 flex items-center gap-3 shadow-sm">
                          <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md font-mono text-sm font-medium">
                            {variable.name}
                          </div>
                          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          <input
                            type="text"
                            value={variable.label}
                            onChange={(e) => updateVariableLabel(index, e.target.value)}
                            className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-0 text-sm transition-colors duration-200"
                            placeholder="表示ラベル"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview Card */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    使い方のヒント
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>変数は {`{{変数名}}`} の形式で入力します</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>クイック変数ボタンでよく使う変数を簡単挿入</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>変数のラベルは自由に変更可能です</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>Ctrl+S または Shift+Enter で保存</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center mt-8 pt-8 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                キャンセル
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-semibold disabled:opacity-50 transition-all duration-200 transform hover:scale-105 flex items-center gap-2 shadow-lg"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    保存中...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    保存する
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}